import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Megaphone,
  MousePointerClick,
  Globe2,
  Link2,
  CalendarRange,
  ArrowRight,
  Lock,
  Smartphone,
  Monitor,
  Tablet,
  Chrome,
  Globe,
  Users,
  UserCheck,
  User,
  Mail,
  Phone,
  AlertTriangle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import {
  Campaign,
  Link as LinkType,
  ClickLog,
  GhlContact,
} from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import RichStatCard from "../../../components/ui/RichStatCard";
import Skeleton from "../../../components/ui/Skeleton";
import EmptyState from "../../../components/ui/EmptyState";
import Badge from "../../../components/ui/Badge";
import LinkCard from "../../../components/ui/LinkCard";
import WorldMap from "../../../components/ui/WorldMap";
import Pagination from "../../../components/ui/Pagination";
import HelpHint from "../../../components/ui/HelpHint";
import { formatDate, formatDateTime, formatNumber } from "../../../utils";

const CLICKS_PER_PAGE = 10;

type Range = "7d" | "30d" | "90d" | "all";
const RANGE_DAYS: Record<Range, number | null> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
};

// ---------------------------------------------------------------------------
// Local helpers (mirrors Analytics/Detail for visual consistency)
// ---------------------------------------------------------------------------

const contactDisplayName = (c?: GhlContact | null) => {
  if (!c) return "";
  return (
    c.contactName ||
    c.name ||
    [c.firstName, c.lastName].filter(Boolean).join(" ") ||
    c.email ||
    c.phone ||
    ""
  );
};

const countBy = <K extends string | undefined | null>(
  logs: ClickLog[],
  getter: (l: ClickLog) => K,
  limit = 6,
): { name: string; value: number }[] => {
  const map = new Map<string, number>();
  for (const log of logs) {
    const key = (getter(log) || "").toString().trim();
    if (!key) continue;
    map.set(key, (map.get(key) || 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
};

const BreakdownList = ({
  items,
  total,
  color,
  emptyLabel,
}: {
  items: { name: string; value: number }[];
  total: number;
  color: string;
  emptyLabel: string;
}) => {
  if (items.length === 0) {
    return (
      <p className="text-sm text-ink-400 text-center py-6">{emptyLabel}</p>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const pct = total > 0 ? (item.value / total) * 100 : 0;
        return (
          <div key={item.name}>
            <div className="flex items-center justify-between text-sm mb-1.5">
              <span className="text-ink-800 truncate pr-2 capitalize font-medium">
                {item.name}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-ink-400 text-xs tabular-nums">
                  {pct.toFixed(1)}%
                </span>
                <span className="text-ink-900 font-semibold text-xs tabular-nums w-7 text-right">
                  {item.value}
                </span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const deviceIcon = (type?: string) => {
  if (type === "mobile") return <Smartphone size={13} />;
  if (type === "tablet") return <Tablet size={13} />;
  return <Monitor size={13} />;
};

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.[0]) return null;
  return (
    <div className="bg-ink-900 text-white rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-ink-400 mb-0.5 text-[10px] uppercase tracking-wide">
        {label}
      </div>
      <div className="font-semibold font-display text-base">
        {payload[0].value}{" "}
        <span className="text-xs font-normal text-ink-400">
          {payload[0].value === 1 ? "click" : "clicks"}
        </span>
      </div>
    </div>
  );
};

const RangeChips = ({
  range,
  setRange,
  retentionDays,
  onLockedClick,
}: {
  range: Range;
  setRange: (r: Range) => void;
  retentionDays: number;
  onLockedClick: () => void;
}) => {
  const items: { value: Range; label: string; days: number }[] = [
    { value: "7d", label: "7d", days: 7 },
    { value: "30d", label: "30d", days: 30 },
    { value: "90d", label: "90d", days: 90 },
    { value: "all", label: "All", days: Infinity },
  ];
  return (
    <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
      {items.map((it) => {
        const locked = it.days > retentionDays;
        const active = range === it.value && !locked;
        return (
          <button
            key={it.value}
            onClick={() => (locked ? onLockedClick() : setRange(it.value))}
            title={
              locked
                ? `Available with longer data retention — upgrade for ${it.label} of history`
                : undefined
            }
            className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-medium transition-all ${
              active
                ? "bg-white text-ink-900 shadow-sm"
                : locked
                ? "text-ink-300"
                : "text-ink-500 hover:text-ink-900"
            }`}
          >
            {locked && <Lock size={10} className="text-ink-400" />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
};

const CardShell = ({
  title,
  subtitle,
  action,
  help,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  help?: { anchor?: string; body: React.ReactNode };
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-white border border-ink-200/70 rounded-2xl shadow-card overflow-hidden ${className}`}
  >
    <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
      <div>
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-ink-900 tracking-tight">
          {title}
          {help && (
            <HelpHint title={title} anchor={help.anchor}>
              {help.body}
            </HelpHint>
          )}
        </h3>
        {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CampaignDetail = () => {
  const { id } = useParams<{ id: string }>();
  const services = useAppServices();
  const { buildPath, features, openUpgrade } = useMarketplace();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [links, setLinks] = useState<LinkType[]>([]);
  const [logs, setLogs] = useState<ClickLog[]>([]);
  const [contacts, setContacts] = useState<Record<string, GhlContact | null>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [range, setRange] = useState<Range>(() =>
    features.dataRetentionDays >= 30 ? "30d" : "7d",
  );

  const fetchStats = useCallback(
    async (showLoading = false) => {
      if (!id) return;
      if (showLoading) setLoading(true);
      const { response } = await services.campaigns.stats({
        query: `_id=${id}`,
      });
      if (!response || !response.campaign) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setCampaign(response.campaign);
      setLinks(response.links || []);
      setLogs(response.logs || []);
      if (showLoading) setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  useEffect(() => {
    fetchStats(true);
  }, [fetchStats]);



  // Resolve GHL contacts for clicks carrying a contact_id (cached, best-effort).
  useEffect(() => {
    const ids = Array.from(
      new Set(logs.map((l) => l.contact_id).filter(Boolean) as string[]),
    ).slice(0, 100);
    const missing = ids.filter((cid) => !(cid in contacts));
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        missing.map(async (cid) => {
          const { response } = await services.ghl.contact({
            query: `id=${encodeURIComponent(cid)}`,
          });
          return [cid, (response as GhlContact) || null] as const;
        }),
      );
      if (cancelled) return;
      setContacts((prev) => {
        const next = { ...prev };
        for (const [cid, c] of results) next[cid] = c;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs]);

  // Apply the time-range filter on top of the (already retention-bounded) logs.
  const filteredLogs = useMemo(() => {
    const days = RANGE_DAYS[range];
    if (days === null) return logs;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return logs.filter((l) => new Date(l.createdAt).getTime() >= cutoff);
  }, [logs, range]);

  const [clicksPage, setClicksPage] = useState(1);
  useEffect(() => {
    setClicksPage(1);
  }, [range]);
  const pagedClicks = useMemo(
    () =>
      filteredLogs.slice(
        (clicksPage - 1) * CLICKS_PER_PAGE,
        clicksPage * CLICKS_PER_PAGE,
      ),
    [filteredLogs, clicksPage],
  );

  // Time-series buckets covering the selected range window.
  const timeSeries = useMemo(() => {
    const days = RANGE_DAYS[range] ?? 30;
    const buckets = new Map<string, number>();
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      buckets.set(d.toISOString().slice(0, 10), 0);
    }
    for (const log of filteredLogs) {
      const k = log.createdAt.slice(0, 10);
      if (buckets.has(k)) buckets.set(k, (buckets.get(k) || 0) + 1);
    }
    return Array.from(buckets.entries()).map(([date, clicks]) => ({
      date: date.slice(5),
      clicks,
    }));
  }, [filteredLogs, range]);

  const sparkData = useMemo(() => timeSeries.map((s) => s.clicks), [timeSeries]);

  // Top performing links sorted by clicks desc.
  const sortedLinks = useMemo(
    () => [...links].sort((a, b) => (b.clicks || 0) - (a.clicks || 0)),
    [links],
  );

  // Aggregations
  const countries = useMemo(
    () => countBy(filteredLogs, (l) => l.country, 8),
    [filteredLogs],
  );
  const countryEntries = useMemo(() => {
    const m = new Map<
      string,
      { name: string; code?: string; value: number }
    >();
    for (const log of filteredLogs) {
      if (!log.country) continue;
      const key = log.country;
      const existing = m.get(key);
      if (existing) existing.value++;
      else m.set(key, { name: key, code: log.country_code, value: 1 });
    }
    return Array.from(m.values());
  }, [filteredLogs]);
  const cities = useMemo(
    () => countBy(filteredLogs, (l) => l.city, 6),
    [filteredLogs],
  );
  const devices = useMemo(
    () => countBy(filteredLogs, (l) => l.device_type, 5),
    [filteredLogs],
  );
  const oses = useMemo(
    () => countBy(filteredLogs, (l) => l.os, 5),
    [filteredLogs],
  );
  const browsers = useMemo(
    () => countBy(filteredLogs, (l) => l.browser, 6),
    [filteredLogs],
  );
  const referrers = useMemo(
    () => countBy(filteredLogs, (l) => l.referrer_platform, 6),
    [filteredLogs],
  );

  const uniqueCountries = useMemo(
    () => new Set(filteredLogs.map((l) => l.country).filter(Boolean)).size,
    [filteredLogs],
  );
  const uniqueVisitors = useMemo(
    () => new Set(filteredLogs.map((l) => l.ip).filter(Boolean)).size,
    [filteredLogs],
  );
  const totalClicks = filteredLogs.length;

  // CRM contact engagement — count only clicks whose contact_id resolved to a
  // real GHL contact (contacts[id] is a found object, not null/undefined).
  const contactClicks = useMemo(
    () => filteredLogs.filter((l) => l.contact_id && contacts[l.contact_id]).length,
    [filteredLogs, contacts],
  );
  const uniqueContacts = useMemo(
    () =>
      new Set(
        filteredLogs
          .filter((l) => l.contact_id && contacts[l.contact_id])
          .map((l) => l.contact_id),
      ).size,
    [filteredLogs, contacts],
  );
  const topContacts = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of filteredLogs) {
      if (!l.contact_id || !contacts[l.contact_id]) continue;
      m.set(l.contact_id, (m.get(l.contact_id) || 0) + 1);
    }
    return Array.from(m.entries())
      .map(([cid, value]) => ({
        name: contactDisplayName(contacts[cid]) || cid,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filteredLogs, contacts]);
  const anyContactClicks = useMemo(
    () => filteredLogs.some((l) => l.contact_id),
    [filteredLogs],
  );
  const hasContactFeatures =
    (campaign?.click_tags?.length || 0) > 0 || anyContactClicks;

  // ----- Loading / not found states -----

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 w-full rounded-3xl" />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (notFound || !campaign) {
    return (
      <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
        <EmptyState
          icon={<Megaphone size={26} />}
          tone="amber"
          title="Campaign not found"
          description="This campaign may have been deleted, or the URL is wrong."
          action={
            <RouterLink
              to={buildPath("campaigns")}
              className="inline-flex items-center gap-2 bg-ink-900 text-white hover:bg-ink-800 h-9 px-3.5 text-sm font-medium rounded-lg"
            >
              Back to campaigns
            </RouterLink>
          }
        />
      </div>
    );
  }

  // ----- Render -----

  return (
    <div className="space-y-6">
      <div>
        <RouterLink
          to={buildPath("campaigns")}
          className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 mb-3"
        >
          <ArrowLeft size={12} /> Back to campaigns
        </RouterLink>
        <HeroSection
          eyebrow={
            <>
              <Megaphone size={11} style={{ color: campaign.color }} />
              <span>Campaign</span>
            </>
          }
          title={campaign.name}
          description={campaign.description || undefined}
        >
          <div className="flex items-center gap-2 flex-wrap pt-2">
            {(campaign.starts_at || campaign.ends_at) && (
              <Badge tone="neutral" icon={<CalendarRange size={10} />}>
                {campaign.starts_at ? formatDate(campaign.starts_at) : "—"} →{" "}
                {campaign.ends_at ? formatDate(campaign.ends_at) : "—"}
              </Badge>
            )}
            {campaign.archived && <Badge tone="amber">Archived</Badge>}
            <Badge tone="brand">
              <span className="font-display font-bold tabular-nums">
                {formatNumber(links.length)}
              </span>
              <span className="text-ink-500 font-normal ml-1">links</span>
            </Badge>
          </div>
        </HeroSection>
      </div>

      {/* Stats */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <RichStatCard
          label="Total clicks"
          value={totalClicks}
          data={sparkData}
          tone="brand"
          icon={<MousePointerClick size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Unique visitors"
          value={uniqueVisitors}
          data={sparkData}
          tone="violet"
          icon={<Users size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Countries"
          value={uniqueCountries}
          data={sparkData}
          tone="emerald"
          icon={<Globe2 size={16} strokeWidth={2.25} />}
        />
        <RichStatCard
          label="Links"
          value={links.length}
          data={[]}
          tone="amber"
          icon={<Link2 size={16} strokeWidth={2.25} />}
        />
      </section>

      {/* Clicks over time */}
      <CardShell
        title="Clicks over time"
        help={{
          anchor: "analytics-time",
          body: "Clicks per day aggregated across every link in this campaign. Use the range chips to change the window. Buckets are in UTC.",
        }}
        subtitle="Aggregated across every link in this campaign."
        action={
          <RangeChips
            range={range}
            setRange={setRange}
            retentionDays={features.dataRetentionDays}
            onLockedClick={openUpgrade}
          />
        }
      >
        {filteredLogs.length === 0 ? (
          <p className="text-sm text-ink-400 text-center py-10">
            No clicks in this window. Try expanding the range or share links to
            start collecting data.
          </p>
        ) : (
          <div className="h-72 -mx-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={timeSeries}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="campGrad"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={campaign.color} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={campaign.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f4f4f5" vertical={false} />
                <XAxis
                  dataKey="date"
                  fontSize={11}
                  stroke="#a1a1aa"
                  tickLine={false}
                  axisLine={false}
                  dy={4}
                />
                <YAxis
                  fontSize={11}
                  stroke="#a1a1aa"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  content={<ChartTooltip />}
                  cursor={{ stroke: "#a1a1aa", strokeDasharray: "3 3" }}
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke={campaign.color}
                  strokeWidth={2.25}
                  fill="url(#campGrad)"
                  animationDuration={600}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardShell>

      {/* Geographic distribution */}
      <CardShell
        title="Geographic distribution"
        help={{
          anchor: "analytics-map",
          body: "Where this campaign's clicks come from, pooled across all its links and shaded by volume. Location is from offline GeoIP data — no third-party tracking.",
        }}
        subtitle={
          uniqueCountries > 0
            ? `${uniqueCountries} ${uniqueCountries === 1 ? "country" : "countries"} reached`
            : "No geo data yet"
        }
      >
        <WorldMap entries={countryEntries} />
      </CardShell>

      {/* Bento breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CardShell title="Top countries">
          <BreakdownList
            items={countries}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No data yet"
          />
        </CardShell>
        <CardShell title="Top cities">
          <BreakdownList
            items={cities}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No city data yet"
          />
        </CardShell>
        <CardShell title="Devices">
          <BreakdownList
            items={devices}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No data yet"
          />
        </CardShell>
        <CardShell title="Operating systems">
          <BreakdownList
            items={oses}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No data yet"
          />
        </CardShell>
        <CardShell title="Browsers">
          <BreakdownList
            items={browsers}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No data yet"
          />
        </CardShell>
        <CardShell title="Referrers">
          <BreakdownList
            items={referrers}
            total={totalClicks}
            color={campaign.color}
            emptyLabel="No referrer data yet"
          />
        </CardShell>
      </div>

      {/* CRM contact engagement */}
      {hasContactFeatures && (
        <CardShell
          title="CRM contact engagement"
          help={{
            anchor: "analytics-contacts",
            body: "Contacts who clicked any link in this campaign — total contact clicks, unique contacts, and your top contacts by clicks (names resolved from your CRM).",
          }}
          subtitle="Contacts who clicked links in this campaign."
          action={
            <Badge tone="brand" dot>
              CRM
            </Badge>
          }
        >
          {(campaign.click_tags?.length || 0) > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-1.5 text-xs text-ink-500 mb-2">
                <UserCheck size={13} className="text-brand-500" />
                Tags applied to contacts on every campaign link
              </div>
              <div className="flex flex-wrap gap-1.5">
                {campaign.click_tags!.map((t) => (
                  <Badge key={t} tone="brand">
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {[
              { label: "Contact clicks", value: contactClicks },
              { label: "Unique contacts", value: uniqueContacts },
              {
                label: "Anonymous clicks",
                value: Math.max(0, totalClicks - contactClicks),
              },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-ink-200/70 bg-ink-50/40 px-3 py-2.5"
              >
                <div className="text-lg font-display font-bold text-ink-900 tabular-nums">
                  {formatNumber(s.value)}
                </div>
                <div className="text-[11px] text-ink-500">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-ink-500 mb-2">Top contacts by clicks</div>
          <BreakdownList
            items={topContacts}
            total={contactClicks}
            color={campaign.color}
            emptyLabel="No identified contacts yet — send links with ?contact_id={{contact.id}}."
          />
        </CardShell>
      )}

      {/* Top performing links */}
      <CardShell
        title="Top performing links"
        help={{
          anchor: "campaigns",
          body: "The links that belong to this campaign, ranked by total clicks — see which member link is pulling the most weight.",
        }}
        subtitle="Member links sorted by total clicks."
        action={
          <RouterLink
            to={buildPath("links")}
            className="inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-900"
          >
            All links <ArrowRight size={12} />
          </RouterLink>
        }
      >
        {sortedLinks.length === 0 ? (
          <EmptyState
            icon={<Link2 size={22} />}
            tone="brand"
            title="No links in this campaign yet"
            description="Edit this campaign and pick links from the list to add them."
          />
        ) : (
          <div className="space-y-2 -mx-2">
            {sortedLinks.map((l) => (
              <LinkCard key={l._id} link={l} />
            ))}
          </div>
        )}
      </CardShell>

      {/* Recent clicks */}
      <CardShell
        title="Recent clicks"
        subtitle={`Latest ${Math.min(filteredLogs.length, 50)} events.`}
      >
        {filteredLogs.length === 0 ? (
          <p className="text-sm text-ink-400 text-center py-6">
            No clicks yet
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 border-b border-ink-100">
                <tr>
                  <th className="text-left px-5 py-3">When</th>
                  <th className="text-left px-5 py-3">Contact</th>
                  <th className="text-left px-5 py-3">Location</th>
                  <th className="text-left px-5 py-3">Device</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">
                    Browser
                  </th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">
                    Referrer
                  </th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100/80">
                {pagedClicks.map((log) => (
                  <tr key={log._id} className="hover:bg-ink-50/60">
                    <td className="px-5 py-2.5 text-ink-700 whitespace-nowrap text-xs">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 text-xs">
                      {log.contact_id ? (
                        (() => {
                          const cid = log.contact_id;
                          const fetched = cid in contacts;
                          const c = contacts[cid];
                          if (!fetched)
                            return <span className="text-ink-400">Loading…</span>;
                          if (!c)
                            return (
                              <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                                <AlertTriangle size={11} className="shrink-0" />
                                Not found
                              </span>
                            );
                          return (
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 font-medium text-ink-800">
                                <User size={11} className="text-brand-500 shrink-0" />
                                <span className="truncate max-w-[150px]">
                                  {contactDisplayName(c) || cid}
                                </span>
                              </div>
                              {c.email && (
                                <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                                  <Mail size={10} className="shrink-0" />
                                  <span className="truncate max-w-[150px]">
                                    {c.email}
                                  </span>
                                </div>
                              )}
                              {c.phone && (
                                <div className="flex items-center gap-1 text-[11px] text-ink-500 mt-0.5">
                                  <Phone size={10} className="shrink-0" />
                                  <span className="truncate max-w-[150px]">
                                    {c.phone}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-ink-700">
                      <div className="flex items-center gap-1.5">
                        {log.country_code && (
                          <span className="text-[10px] font-mono uppercase text-ink-500 bg-ink-100 px-1.5 py-0.5 rounded">
                            {log.country_code}
                          </span>
                        )}
                        <span className="text-xs">
                          {[log.city, log.country].filter(Boolean).join(", ") ||
                            "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 text-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="text-ink-400">
                          {deviceIcon(log.device_type)}
                        </span>
                        <span className="capitalize">
                          {log.device_type || "?"}
                        </span>
                        <span className="text-ink-400">· {log.os || "?"}</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 hidden md:table-cell text-xs">
                      <div className="flex items-center gap-1.5">
                        {/Chrome/i.test(log.browser || "") ? (
                          <Chrome size={12} className="text-ink-400" />
                        ) : (
                          <Globe size={12} className="text-ink-400" />
                        )}
                        {log.browser || "?"}
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-ink-700 hidden lg:table-cell text-xs">
                      {log.referrer_platform || "Direct"}
                    </td>
                    <td className="px-5 py-2.5 text-ink-500 font-mono text-xs hidden md:table-cell">
                      {log.ip || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={clicksPage}
              pageSize={CLICKS_PER_PAGE}
              total={filteredLogs.length}
              onPage={setClicksPage}
              className="px-5 pt-4"
            />
          </div>
        )}
      </CardShell>
    </div>
  );
};

export default CampaignDetail;
